import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { LogoutButton } from '../components/LogoutButton';
import { NotificationsBell } from '../components/NotificationsBell';
import './PaymentSuccess.css';
import {
  Check, AlertCircle, Clock, Zap, ArrowLeft, ArrowRight,
  Receipt, Hash, Calendar, DollarSign, CreditCard, RefreshCw,
} from 'lucide-react';

const APP_NAME = import.meta.env.VITE_APP_NAME || 'Voltary';

interface PaymentData {
  id: string;
  amount: number;
  orderId: string;
  status: 'completed' | 'pending';
  createdAt: string;
}

type ViewStatus = 'loading' | 'success' | 'pending' | 'error';

export function PaymentSuccess() {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('orderId');
  const [status, setStatus] = useState<ViewStatus>('loading');
  const [payment, setPayment] = useState<PaymentData | null>(null);

  useEffect(() => {
    if (orderId) {
      fetchPaymentData();
    } else {
      setStatus('pending');
    }
  }, [orderId]);

  // Auto-refresh cada 15s mientras esté pendiente
  useEffect(() => {
    if (status === 'pending' && orderId) {
      const interval = setInterval(() => fetchPaymentData(), 15000);
      return () => clearInterval(interval);
    }
  }, [status, orderId]);

  const fetchPaymentData = async () => {
    try {
      const res = await fetch(`${import.meta.env.VITE_BACKEND_URL}/api/payment/${orderId}`, {
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        setPayment(data.payment);
        setStatus(data.payment.status === 'completed' ? 'success' : 'pending');
      } else {
        setStatus('pending');
      }
    } catch (err) {
      console.error('Error fetching payment:', err);
      setStatus('pending');
    }
  };

  // ============== RENDER ==============

  // Topbar reutilizable para todos los estados
  const Topbar = () => (
    <header className="sp-topbar">
      <div className="sp-topbar-left">
        <Link to="/dashboard" className="sp-topbar-brand">
          <Zap size={16} />
          <span>{APP_NAME}</span>
        </Link>
        <span className="sp-topbar-divider" aria-hidden="true" />
        <span className="sp-topbar-crumb">Comprobante de pago</span>
      </div>
      <div className="sp-topbar-right">
        <NotificationsBell />
        <LogoutButton />
      </div>
    </header>
  );

  // Loading
  if (status === 'loading') {
    return (
      <div className="db-page">
        <Topbar />
        <main className="db-main">
          <div className="db-loading">
            <div className="spinner" />
            <p>Procesando pago…</p>
          </div>
        </main>
      </div>
    );
  }

  // Pendiente (auto-refresh) o error
  if (status === 'pending' || status === 'error') {
    const isError = status === 'error';
    return (
      <div className="db-page">
        <Topbar />
        <main className="db-main">
          <section className={`ps-pending-hero ${isError ? 'ps-pending-hero--error' : ''}`}>
            <div className="ps-pending-icon">
              {isError ? <AlertCircle size={48} /> : <Clock size={48} />}
              {!isError && <div className="ps-pending-pulse" aria-hidden="true" />}
            </div>
            <h1 className="ps-pending-title">
              {isError ? 'Error en el pago' : 'Pago en proceso'}
            </h1>
            <p className="ps-pending-sub">
              {isError
                ? 'No se pudo procesar tu pago. Por favor intenta nuevamente.'
                : 'Tu pago está siendo procesado. Esto puede tardar unos minutos.'}
            </p>
            {orderId && (
              <div className="ps-pending-order">
                <span className="ps-pending-order-label">Comprobante</span>
                <code className="ps-pending-order-code">{orderId}</code>
              </div>
            )}
            <div className="ps-pending-actions">
              {!isError ? (
                <button type="button" className="btn btn-primary" onClick={() => window.location.reload()}>
                  <RefreshCw size={14} /> Actualizar ahora
                </button>
              ) : (
                <Link to="/buy-credits" className="btn btn-primary">
                  <CreditCard size={14} /> Intentar de nuevo
                </Link>
              )}
              <Link to="/dashboard" className="btn btn-ghost">
                <ArrowLeft size={14} /> Volver al Dashboard
              </Link>
            </div>
            {!isError && (
              <p className="ps-pending-auto">
                <RefreshCw size={11} /> Se actualiza automáticamente cada 15 segundos
              </p>
            )}
          </section>
        </main>
      </div>
    );
  }

  // Success
  return (
    <div className="db-page">
      <Topbar />
      <main className="db-main db-main--compact">
        {/* HERO compacto (vertical, full-width) */}
        <section className="ps-hero">
          <div className="ps-hero-checkmark">
            <div className="ps-checkmark-circle">
              <Check size={32} strokeWidth={3} />
            </div>
            <div className="ps-checkmark-pulse" aria-hidden="true" />
          </div>

          <h1 className="ps-hero-title">Pago completado</h1>
          <p className="ps-hero-sub">
            Tu transacción fue procesada exitosamente.
          </p>

          {payment && (
            <div className="ps-hero-amount">
              <span className="ps-hero-amount-prefix">+</span>
              <span className="ps-hero-amount-currency">$</span>
              {payment.amount.toFixed(2)}
              <span className="ps-hero-amount-suffix">USD</span>
            </div>
          )}
        </section>

        {/* Panel de detalles (debajo del hero) */}
        <section className="sp-panel ps-success-details">
          <div className="sp-panel-body">
            <div className="sp-info-list">
              <div className="sp-info-row">
                <span className="sp-info-row-label">
                  <Hash size={12} /> ID de orden
                </span>
                <span className="sp-info-row-value">
                  <code className="ps-order-code">{orderId || '—'}</code>
                </span>
              </div>

              {payment && (
                <>
                  <div className="sp-info-row">
                    <span className="sp-info-row-label">
                      <DollarSign size={12} /> Monto
                    </span>
                    <span className="sp-info-row-value sp-info-price">
                      +${payment.amount.toFixed(2)} USD
                    </span>
                  </div>

                  <div className="sp-info-row">
                    <span className="sp-info-row-label">
                      <Calendar size={12} /> Fecha
                    </span>
                    <span className="sp-info-row-value">
                      {new Date(payment.createdAt).toLocaleString('es-ES', {
                        year: 'numeric', month: 'long', day: 'numeric',
                        hour: '2-digit', minute: '2-digit',
                      })}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="sp-panel-footer sp-panel-footer--actions">
            <Link to="/dashboard" className="btn btn-primary">
              <ArrowLeft size={14} /> Ir al Dashboard
            </Link>
            <Link to="/buy-credits" className="btn btn-ghost">
              <CreditCard size={14} /> Recargar otra vez
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
